import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";

const GalleryPage = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [filter, setFilter] = useState("all");

  const galleryItems = [
    {
      id: 1,
      category: "polishing",
      title: "BMW M5 Polírozás",
      before: "https://images.unsplash.com/photo-1555215695-3004980ad54e?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1555215695-3004980ad54e?q=80&w=800"
    },
    {
      id: 2,
      category: "ceramic",
      title: "Porsche 911 Kerámia Bevonat",
      before: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800"
    },
    {
      id: 3,
      category: "interior",
      title: "Mercedes S-Class Belső",
      before: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=800"
    },
    {
      id: 4,
      category: "detailing",
      title: "Audi RS6 Teljes Detailing",
      before: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?q=80&w=800"
    },
    {
      id: 5,
      category: "polishing",
      title: "Ferrari F8 Lakkorrekció",
      before: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?q=80&w=800"
    },
    {
      id: 6,
      category: "ceramic",
      title: "Lamborghini Urus Védelem",
      before: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?q=80&w=800&sat=-100",
      after: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?q=80&w=800"
    },
  ];

  const categories = [
    { id: "all", label: "Összes" },
    { id: "polishing", label: "Polírozás" },
    { id: "ceramic", label: "Kerámia" },
    { id: "interior", label: "Belső" },
    { id: "detailing", label: "Detailing" },
  ];

  const filteredItems = filter === "all" 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);
  
  const nextImage = () => {
    if (selectedIndex < filteredItems.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };
  
  const prevImage = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="gallery-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Galéria
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              ELŐTTE – UTÁNA
              <span className="block text-[#D4AF37]">GALÉRIÁNK</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Tekintse meg munkáinkat és győződjön meg a minőségről. Húzza a csúszkát 
              az előtte-utána összehasonlításhoz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-[#0A0A0A] border-t border-[#2A2A2A]">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-6 py-2 rounded-sm uppercase tracking-widest text-sm transition-all ${
                  filter === cat.id
                    ? "bg-[#D4AF37] text-black"
                    : "bg-transparent border border-[#2A2A2A] text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="relative border border-[#2A2A2A] hover:border-[#D4AF37] transition-colors overflow-hidden">
                    <ReactCompareSlider
                      itemOne={
                        <ReactCompareSliderImage
                          src={item.before}
                          alt={`${item.title} előtte`}
                        />
                      }
                      itemTwo={
                        <ReactCompareSliderImage
                          src={item.after}
                          alt={`${item.title} utána`}
                        />
                      }
                      style={{ height: "280px" }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#050505] to-transparent p-4">
                      <h3 className="font-heading text-[#E5E5E5] text-lg">{item.title}</h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#050505]/95 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-[#E5E5E5] hover:text-[#D4AF37] transition-colors"
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            {selectedIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 md:left-8 text-[#E5E5E5] hover:text-[#D4AF37] transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={48} />
              </button>
            )}

            {selectedIndex < filteredItems.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 md:right-8 text-[#E5E5E5] hover:text-[#D4AF37] transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight size={48} />
              </button>
            )}

            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <ReactCompareSlider
                itemOne={
                  <ReactCompareSliderImage
                    src={filteredItems[selectedIndex].before}
                    alt="Előtte"
                  />
                }
                itemTwo={
                  <ReactCompareSliderImage
                    src={filteredItems[selectedIndex].after}
                    alt="Utána"
                  />
                }
                style={{ height: "70vh", maxHeight: "600px" }}
                className="border border-[#2A2A2A] rounded-lg"
              />
              <div className="mt-4 text-center">
                <h3 className="font-heading text-2xl text-[#E5E5E5]">
                  {filteredItems[selectedIndex].title}
                </h3>
                <div className="flex justify-between mt-2 text-sm">
                  <span className="text-[#A3A3A3]">Előtte</span>
                  <span className="text-[#D4AF37]">Utána</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
